import { showToast } from '../components/ui/toast'
import { getCsrfToken } from './csrf'

interface JsonResponse {
  notice?: string | null
  alert?: string | null
  [key: string]: unknown
}

/**
 * A setting that takes effect the moment it moves.
 *
 * Nothing in the redesigned Settings has a Save button — the page says "Saved
 * automatically" and means it — so every toggle, picker and segmented control
 * comes through here: write, confirm with a toast, and let the caller put the
 * control back where it was if the server said no.
 *
 * The same job as lib/submitJson.ts, which takes a <form> and raises the older
 * stacking toast. This one takes a plain object, because the new controls are
 * buttons rather than inputs and building a hidden form around each of them was
 * the only reason they had one.
 */
export async function writeSetting(
  action: string,
  fields: Record<string, string | number | boolean | null | string[] | File>,
  { method = 'patch' }: { method?: 'patch' | 'post' | 'delete' } = {}
): Promise<JsonResponse | null> {
  const body = new FormData()
  // Rails reads the verb from _method on a POST, same as a plain form would.
  body.set('_method', method)
  body.set('authenticity_token', getCsrfToken())
  Object.entries(fields).forEach(([key, value]) => {
    // An array key is appended once per member, the way a repeated form field
    // arrives — and an *empty* array still appends one blank, because "none of
    // them" and "this form never asked" are different answers and the server
    // treats them differently. See SettingsController#notification_params.
    if (Array.isArray(value)) {
      if (value.length === 0) body.append(key, '')
      else value.forEach((member) => body.append(key, member))
      return
    }
    // A File goes in as itself — FormData is already multipart, so a picture
    // rides along with the same write-on-change path every other setting uses.
    if (value instanceof File) body.set(key, value)
    else body.set(key, value === null ? '' : String(value))
  })

  let response: Response
  try {
    response = await fetch(action, { method: 'POST', body, headers: { Accept: 'application/json' } })
  } catch {
    // Offline, or the request never left. Distinct from a refusal, and the
    // caller rolls back for both.
    showToast('That did not save — check your connection.', 'fail')
    return null
  }

  let data: JsonResponse = {}
  try {
    data = await response.json()
  } catch {
    showToast('Something went wrong saving that.', 'fail')
    return null
  }

  // `alert` is the server refusing; `notice` is it agreeing. They have looked
  // identical since the redesign's toast landed, which is how "That code didn't
  // work" came to arrive under a tick.
  if (data.alert) showToast(data.alert, 'fail')
  else if (data.notice) showToast(data.notice)

  return response.ok ? data : null
}
